// Prompt preview helpers.
//
// A prompt value is one of two shapes:
//   1. Markdown / plain text  -> VariableMentionEditor keeps the raw text.
//   2. HTML                   -> vix Editor is bound with output="html".
//
// markdown-it is configured with html: true, so a value that starts with a block
// tag is passed through as a raw HTML block and its Markdown syntax is never
// parsed. HTML values are therefore flattened back to Markdown source first, so
// `##`, `-` and `**` typed inside the rich editor still render as Markdown.

const BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'div',
  'dl',
  'dt',
  'dd',
  'figcaption',
  'figure',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul'
]);

// Only real HTML tag names count, so Markdown such as `<https://a.b>` or
// `a < b` is not mistaken for HTML.
const KNOWN_TAGS = new Set([
  ...BLOCK_TAGS,
  'a',
  'b',
  'br',
  'code',
  'del',
  'em',
  'i',
  'img',
  'mark',
  's',
  'span',
  'strike',
  'strong',
  'sub',
  'sup',
  'u'
]);

const HTML_TAG_PATTERN = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;

const ZERO_WIDTH_PATTERN = /[\u200B-\u200D\uFEFF]/g;

const INDENT_UNIT = '  ';

export const looksLikeHtml = value => {
  const source = String(value || '');
  if (!source.includes('<')) return false;
  HTML_TAG_PATTERN.lastIndex = 0;
  let match = HTML_TAG_PATTERN.exec(source);
  while (match) {
    if (KNOWN_TAGS.has(match[1].toLowerCase())) return true;
    match = HTML_TAG_PATTERN.exec(source);
  }
  return false;
};

const serializeChildren = (node, context) => {
  Array.from(node.childNodes).forEach(child => serializeNode(child, context));
};

const serializeNode = (node, context) => {
  if (node.nodeType === 3) {
    context.out.push(node.textContent.replace(ZERO_WIDTH_PATTERN, ''));
    return;
  }
  if (node.nodeType !== 1) return;

  const tag = node.tagName.toLowerCase();

  if (tag === 'br') {
    context.out.push(`\n${context.indent}`);
    return;
  }

  if (tag === 'hr') {
    context.out.push(`\n\n${context.indent}---\n\n${context.indent}`);
    return;
  }

  if (tag === 'pre') {
    const code = node.textContent.replace(/\n+$/, '');
    context.out.push(
      `\n\n${context.indent}\`\`\`\n${code}\n${context.indent}\`\`\`\n\n${context.indent}`
    );
    return;
  }

  if (/^h[1-6]$/.test(tag)) {
    const level = Number(tag[1]);
    context.out.push(`\n\n${context.indent}${'#'.repeat(level)} `);
    serializeChildren(node, context);
    context.out.push(`\n\n${context.indent}`);
    return;
  }

  if (tag === 'li') {
    context.out.push(`\n${context.indent}- `);
    serializeChildren(node, {
      ...context,
      indent: context.indent + INDENT_UNIT,
      inListItem: true
    });
    return;
  }

  if (tag === 'ul' || tag === 'ol') {
    context.out.push(`\n${context.indent}`);
    serializeChildren(node, { ...context, inListItem: false });
    context.out.push(`\n${context.indent}`);
    return;
  }

  if (tag === 'blockquote') {
    context.out.push(`\n\n${context.indent}> `);
    serializeChildren(node, { ...context, inListItem: false });
    context.out.push(`\n\n${context.indent}`);
    return;
  }

  if (BLOCK_TAGS.has(tag)) {
    // Paragraphs nested in a list item stay on the item line instead of
    // breaking the list apart with blank lines.
    const tight = Boolean(context.inListItem);
    if (!tight) context.out.push(`\n\n${context.indent}`);
    serializeChildren(node, { ...context, inListItem: false });
    context.out.push(tight ? `\n${context.indent}` : `\n\n${context.indent}`);
    return;
  }

  // Inline elements stay as raw HTML (mention chips, links, images, emphasis
  // produced by the rich editor), which markdown-it renders with html: true.
  context.out.push(node.outerHTML);
};

/**
 * Normalize a prompt value into Markdown source that markdown-it can parse.
 * Plain Markdown text is returned unchanged; HTML is flattened back to Markdown.
 */
export const toPreviewMarkdown = value => {
  const source = String(value || '');
  if (!source || !looksLikeHtml(source) || typeof DOMParser === 'undefined') {
    return source;
  }

  const doc = new DOMParser().parseFromString(source, 'text/html');
  const context = { indent: '', inListItem: false, out: [] };
  serializeChildren(doc.body, context);

  return context.out
    .join('')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
